var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { styled } from 'styled-components';
import { Spinner, Up } from '@hn-ui/icons';
import { useAppGoogleAnalytics } from '../../../../../shared_app/hooks/useAppGoogleAnalytics';
import { SkillStatus, TitleByStatus } from '../../constants/statuses';
import { useScrollDetector } from '../../hooks/useScrollDetector';
import { useStudentSkills } from '../../hooks/useStudentSkills';
import Tabs from '../Tabs';
import { StatusDetails } from './StatusDetails';
import { Summary } from './Summary';
import { ROW_HEIGHT, SCROLL_OFFSET_Y, SUB_ROW_PADDING_TOP, } from '../../constants/table';
import { SkillsReportEvents, useSkillsReportAnalytics, } from '../../hooks/useSkillsReportAnalytics';
var ScWrapper = styled.div.withConfig({ displayName: "ScWrapper", componentId: "sc-z48gyw" })(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  padding: 32px;\n  background: ", ";\n  box-shadow: inset 0px 16px 16px -16px rgba(0, 0, 0, 0.1);\n"], ["\n  padding: 32px;\n  background: ", ";\n  box-shadow: inset 0px 16px 16px -16px rgba(0, 0, 0, 0.1);\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.darkBlue99;
});
var ScInner = styled.div.withConfig({ displayName: "ScInner", componentId: "sc-7qzkp" })(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  padding-top: 32px;\n  min-height: 207px;\n  display: flex;\n"], ["\n  padding-top: 32px;\n  min-height: 207px;\n  display: flex;\n"])));
var ScScrollBtn = styled.button.withConfig({ displayName: "ScScrollBtn", componentId: "sc-lpja5d" })(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: absolute;\n  bottom: 32px;\n  right: 36px;\n  color: ", ";\n  background: transparent;\n  border: none;\n  padding: 0;\n  cursor: pointer;\n\n  :hover {\n    color: ", ";\n\n    svg {\n      fill: ", ";\n    }\n  }\n"], ["\n  position: absolute;\n  bottom: 32px;\n  right: 36px;\n  color: ", ";\n  background: transparent;\n  border: none;\n  padding: 0;\n  cursor: pointer;\n\n  :hover {\n    color: ", ";\n\n    svg {\n      fill: ", ";\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.blue50;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.blue40;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.blue40;
});
var ScWrapperLoader = styled(ScWrapper).withConfig({ displayName: "ScWrapperLoader", componentId: "sc-1td35tf" })(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  text-align: center;\n"], ["\n  text-align: center;\n"])));
var DETAILED_STATUSES_IN_ORDER = [
    SkillStatus.HelpNeeded,
    SkillStatus.Developing,
    SkillStatus.Mastered,
];
var isDetailedStatus = function (status) {
    return DETAILED_STATUSES_IN_ORDER.includes(status);
};
export var StudentDetails = function (_a) {
    var student = _a.student, domain = _a.domain, grades = _a.grades, activeStatus = _a.activeStatus, onLoad = _a.onLoad;
    var studentId = student.id, skills = student.skills;
    var tabs = React.useMemo(function () {
        return DETAILED_STATUSES_IN_ORDER.map(function (status) {
            var skillsCount = skills.filter(function (el) { return el.status === status; }).length;
            return {
                id: status,
                name: TitleByStatus[status],
                counter: skillsCount,
            };
        }).concat([{ id: 'Summary', name: 'Summary' }]);
    }, [skills]);
    var initTab = React.useMemo(function () {
        if (activeStatus && isDetailedStatus(activeStatus))
            return tabs.find(function (el) { return el.id === activeStatus; });
        var notEmptyTab = tabs.find(function (el) { return Number(el.counter) > 0; });
        if (notEmptyTab)
            return notEmptyTab;
        return tabs[0];
    }, [tabs, activeStatus]);
    var _b = React.useState(initTab), activeTab = _b[0], setActiveTab = _b[1];
    var _c = useStudentSkills({
        studentId: studentId,
        domainId: domain.id,
    }), data = _c.response, isLoading = _c.isLoading;
    var ga4 = useAppGoogleAnalytics();
    var sendGaEvent = useSkillsReportAnalytics();
    var onTabClick = React.useCallback(function (tab) {
        setActiveTab(tab);
        switch (tab.id) {
            case SkillStatus.Mastered:
                sendGaEvent(SkillsReportEvents.studentDetailsMasteredTabClick);
                ga4 === null || ga4 === void 0 ? void 0 : ga4.sendEventAsSupervizor({
                    action: 'click_skills_levels_for_student_mastered_supervizor',
                    category: 'Skills Report',
                });
                break;
            case SkillStatus.Developing:
                sendGaEvent(SkillsReportEvents.studentDetailsDevelopingTabClick);
                ga4 === null || ga4 === void 0 ? void 0 : ga4.sendEventAsSupervizor({
                    action: 'click_skills_levels_for_student_developing_supervizor',
                    category: 'Skills Report',
                });
                break;
            case SkillStatus.HelpNeeded:
                sendGaEvent(SkillsReportEvents.studentDetailsHelpNeededTabClick);
                ga4 === null || ga4 === void 0 ? void 0 : ga4.sendEventAsSupervizor({
                    action: 'click_skills_levels_for_student_need_help_supervizor',
                    category: 'Skills Report',
                });
                break;
            case 'Summary':
                sendGaEvent(SkillsReportEvents.studentDetailsSummaryTabClick);
                ga4 === null || ga4 === void 0 ? void 0 : ga4.sendEventAsSupervizor({
                    action: 'click_skills_levels_for_student_summary_supervizor',
                    category: 'Skills Report',
                });
                break;
            default:
                break;
        }
    }, [ga4, sendGaEvent]);
    React.useEffect(function () {
        setActiveTab(initTab);
    }, [initTab]);
    var currentSkills = React.useMemo(function () {
        if (!data)
            return [];
        return data.skills.filter(function (el) { return el.status === activeTab.id; }).reverse();
    }, [activeTab, data]);
    var containerRef = React.useRef(null);
    var _d = useScrollDetector(containerRef, SCROLL_OFFSET_Y + ROW_HEIGHT + SUB_ROW_PADDING_TOP), needScroll = _d.needScroll, scrollToElement = _d.scrollToElement;
    var handleScrollClick = React.useCallback(function () {
        scrollToElement();
        sendGaEvent(SkillsReportEvents.studentDetailsScrollToTopClick);
    }, [sendGaEvent, scrollToElement]);
    React.useEffect(function () {
        if (containerRef.current && data && !isLoading)
            onLoad === null || onLoad === void 0 ? void 0 : onLoad();
    }, [data, isLoading, onLoad]);
    if (!data)
        return (_jsx(ScWrapperLoader, { children: _jsx(Spinner, { size: 40 }, void 0) }, void 0));
    return (_jsxs(ScWrapper, __assign({ ref: containerRef, "data-testid": "student-details-" + student.id }, { children: [_jsx(Tabs, { tabs: tabs, onClick: onTabClick, initActiveTab: initTab }, void 0), _jsx(ScInner, { children: activeTab.id === 'Summary' ? (_jsx(Summary, { domainName: domain.name, grades: grades, studentSkills: data.skills, summary: data.summary }, void 0)) : (_jsx(StatusDetails, { studentId: studentId, statusName: activeTab.name, skills: currentSkills, domain: domain }, void 0)) }, void 0), needScroll && (_jsx(ScScrollBtn, __assign({ type: 'button', onClick: handleScrollClick, title: 'Scroll to top' }, { children: _jsx(Up, { size: 24, color: 'blue60' }, void 0) }), void 0))] }), void 0));
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
